import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { from } from 'rxjs';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { AuthService } from '@/core/auth/auth.service';
import { LoggerService } from '@/core/services/logger.service';
import { CertificadoRegularidadePjService } from '@/core/api/sicom-api/registro/prestador/relatorios/certificado-regularidade-pj/certificado-regularidade-pj.service';
import { AssinantePjResponse, ReportCertificadoRegularidadePjRequest, ReportCertificadoRegularidadePjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/certificado-regularidade-pj/certificado-regularidade-pj.model';
import { CertificadoRegularidadePjJobService } from '@/core/services/certificado-regularidade-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportCertificadoRegularidadePjRequest;
type Resp = ReportCertificadoRegularidadePjResponse;

interface CertificadoRegularidadePjFormControls {
    observacoes: FormControl<string | null>;
    idAssinante: FormControl<number | null>;
    assinanteObj: FormControl<AssinantePjResponse | null>;
}

@Component({
    selector: 'app-certificado-regularidade-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, TextareaModule, SelectEstado, RelatorioContainer, SelectModule, CanDirective],
    templateUrl: './certificado-regularidade-pj.html',
    styleUrl: './certificado-regularidade-pj.scss'
})
/**
 * Relatório PDF assíncrono: Certificado de Regularidade de Inscrição PJ (prestador).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui resolvemos a
 * identificação do prestador (via `PessoaService`) e a seleção do assinante a partir da
 * diretoria vigente do CRM da UF (QryAssinantes do legado), com nome + cargo.
 */
export class CertificadoRegularidadePjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly formBuilder = inject(FormBuilder);
    private readonly certificadoService = inject(CertificadoRegularidadePjService);
    private readonly authService = inject(AuthService);
    private readonly pessoaService = inject(PessoaService);
    private readonly logger = inject(LoggerService);
    readonly certificadoJobService = inject(CertificadoRegularidadePjJobService);

    readonly job$ = this.certificadoJobService.job$;

    form: FormGroup<CertificadoRegularidadePjFormControls> = this.formBuilder.group<CertificadoRegularidadePjFormControls>({
        observacoes: new FormControl<string | null>(''),
        idAssinante: new FormControl<number | null>(null, Validators.required),
        assinanteObj: new FormControl<AssinantePjResponse | null>(null, Validators.required)
    });

    ufAssinante = '';
    ufAssinanteTocada = false;

    assinantes = signal<AssinantePjResponse[]>([]);
    loadingAssinantes = signal(false);

    private usuarioNome = '';

    protected get jobFacade(): RelatorioJobFacade {
        return this.certificadoJobService;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.inicializarUsuarioLogado();
    }

    /** Resolve o nome do usuário logado (usado como emissor do certificado). */
    private inicializarUsuarioLogado(): void {
        from(this.authService.getUserInfo()).subscribe((userInfo) => {
            this.usuarioNome = userInfo.name || '';
        });
    }

    /** Carrega a diretoria vigente da UF (QryAssinantes do legado). */
    private carregarAssinantes(uf: string): void {
        if (!uf) {
            this.assinantes.set([]);
            return;
        }

        this.loadingAssinantes.set(true);
        this.certificadoService.listarAssinantes(uf).subscribe({
            next: (lista) => {
                this.assinantes.set(lista ?? []);
                if ((lista ?? []).length === 1) {
                    const assinante = lista[0];
                    this.ufAssinante = assinante.sgUf;
                    this.form.patchValue({
                        assinanteObj: assinante,
                        idAssinante: assinante.idSolicitante
                    });
                }
                this.loadingAssinantes.set(false);
            },
            error: (err) => {
                this.logger.error('Erro ao carregar assinantes do certificado PJ', err);
                this.assinantes.set([]);
                this.loadingAssinantes.set(false);
            }
        });
    }

    /** Rótulo do dropdown: "Nome — Cargo" (ou só o nome quando não há cargo). */
    rotuloAssinante(a: AssinantePjResponse): string {
        return a.cargo ? `${a.nome} — ${a.cargo}` : a.nome;
    }

    onAssinanteChange(assinante: AssinantePjResponse | null): void {
        if (assinante) {
            this.ufAssinante = assinante.sgUf;
            this.form.patchValue({ idAssinante: assinante.idSolicitante });
        } else {
            this.form.patchValue({ idAssinante: null });
        }
    }

    alterarUfAssinante(uf: string | null): void {
        this.ufAssinante = uf ?? '';
        this.form.patchValue({ assinanteObj: null, idAssinante: null });
        this.assinantes.set([]);

        if (this.ufAssinante) {
            this.carregarAssinantes(this.ufAssinante);
        }
    }

    marcarUfAssinanteTocada(): void {
        this.ufAssinanteTocada = true;
    }

    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        this.ufAssinanteTocada = true;

        if (this.form.invalid || !this.ufAssinante) return null;

        const { observacoes, idAssinante } = this.form.getRawValue();

        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar o certificado.'
            });
            return null;
        }

        const assinanteObj = this.form.get('assinanteObj')?.value ?? null;

        return {
            nuCrm,
            sgUf,
            assinante: assinanteObj?.nome || '',
            idAssinante: idAssinante ?? undefined,
            sgUfAssinante: this.ufAssinante,
            observacoes: observacoes || '',
            emissor: this.usuarioNome || (this.authService.identityClaims?.['preferred_username'] as string) || 'Sistema'
        };
    }

    protected exportar(request: Req) {
        return this.certificadoService.exportarPdf(request);
    }

    limparFormulario(): void {
        const assinante = this.form.get('assinanteObj')?.value ?? null;
        this.form.reset({ observacoes: '', idAssinante: null, assinanteObj: null });

        // Mantém o assinante selecionado se ele existir.
        if (assinante) {
            this.form.patchValue({ assinanteObj: assinante, idAssinante: assinante.idSolicitante });
        }

        this.ufAssinanteTocada = false;
        this.limparJob();
    }
}
